import React, { useState } from "react";
import {
  FileUploaderComp,
  ImageLoaderUpdate,
  SelectComp2,
  TextInputComp,
  CardComp,
} from "../../../../components";
import { Formik, Form } from "formik";
import { useAddBranchMutation } from "../../../../store/admin/settings/branchSlice";
import {
  useGetCountriesQuery,
  useGetCitiesQuery,
} from "../../../../store/selectableSlice";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { PageTitleWithNav } from "../../modules/PageHeaders";

export default function CreateBranch({ onNext, onBack }) {
  const [addNewBranch, { isError, isLoading, isSuccess }] =
    useAddBranchMutation();

  // State for dropdown selections
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [branchPhoto, setBranchPhoto] = useState(null);
  const [useCooperativeLogo, setUseCooperativeLogo] = useState(false);

  // Get countries and cities data (backend now returns sorted alphabetically)
  const { data: countries = [], isLoading: isLoadingCountries } =
    useGetCountriesQuery();
  const { data: cities = [], isLoading: isLoadingCities } = useGetCitiesQuery();

  // Filter cities based on selected country (backend already sorts alphabetically)
  const filteredCities = selectedCountry
    ? cities.filter((city) => city.country_id === selectedCountry.value)
    : [];

  const handleCountryChange = (countrySelected) => {
    setSelectedCountry(countrySelected);
    setSelectedCity(null); // Reset city when country changes
  };

  const handleCityChange = (citySelected) => {
    setSelectedCity(citySelected);
  };

  const handlePhotoUpload = (photoUrl) => {
    setBranchPhoto(photoUrl);
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Branch name is required"),
    phone: Yup.string()
      .matches(
        /^0[0-9]{10}$/,
        "Phone number must be eleven digits starting with 0"
      )
      .required("Phone number is required"),
    email: Yup.string()
      .email("Field must be a valid email")
      .required("Email is required"),
    address: Yup.string().required("Address is required"),
  });

  const initialValues = {
    name: "",
    email: "",
    address: "",
    phone: "",
    country_id: "",
    city_id: "",
    photo: "",
  };
  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    // Validate that country and city are selected
    if (!selectedCountry || !selectedCity) {
      toast.error("Please select both country and city");
      return;
    }

    const reqBody = {
      name: values.name,
      phone: values.phone,
      email: values.email,
      address: values.address,
      country_id: selectedCountry.value,
      city_id: selectedCity.value,
      photo: useCooperativeLogo ? null : branchPhoto, // If using cooperative logo, don't send custom photo
      use_cooperative_logo: useCooperativeLogo,
    };

    try {
      setSubmitting(true);
      const toastId = toast.loading("Creating branch...");

      const response = await addNewBranch(reqBody);

      if ("error" in response) {
        const { data } = response?.error;
        toast.error(data?.message || "An error occurred", { id: toastId });
        return;
      }

      const { message } = response?.data;
      toast.success(message || "Branch created successfully!", { id: toastId });

      // Reset form and state
      resetForm();
      setSelectedCountry(null);
      setSelectedCity(null);
      setBranchPhoto(null);
      setUseCooperativeLogo(false);
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      <PageTitleWithNav title={"Create Branches"} action={onBack} />
      <div>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form>
              {Object.keys(errors).length > 0 && (
                <div className="alert alert-danger mb-3">
                  <strong>Please fix the following errors:</strong>
                  <ul className="mb-0 mt-2">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <CardComp
                cardClassName={"pt-4"}
                cardBodyClassName={"py-0 row gx-5 gy-3 justify-content-between"}
              >
                <div className="col-12 col-lg-5">
                  <div className="mb-3">
                    <TextInputComp
                      LabelText={"Branch name"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-2"}
                      placeholder={"Input branch name"}
                      name="name"
                    />
                  </div>
                  <div className="mb-3">
                    <SelectComp2
                      LabelClassName={"fw-2"}
                      labelText={"Country"}
                      arrayComp={[
                        { value: "", label: "Select Country" },
                        ...countries.map((country) => ({
                          value: country.id,
                          label: country.name,
                        })),
                      ]}
                      setSelectedOption={handleCountryChange}
                      isLoading={isLoadingCountries}
                      isSearchable={true}
                    />
                  </div>
                  <div className="mb-3">
                    <SelectComp2
                      LabelClassName={"fw-2"}
                      labelText={"City/State"}
                      arrayComp={[
                        { value: "", label: "Select City/State" },
                        ...filteredCities.map((city) => ({
                          value: city.id,
                          label: city.name,
                        })),
                      ]}
                      setSelectedOption={handleCityChange}
                      isLoading={isLoadingCities}
                      disabled={!selectedCountry}
                      isSearchable={true}
                    />
                  </div>
                  <div className="mb-3">
                    <TextInputComp
                      LabelText={"Phone number"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-2"}
                      placeholder={"Enter Phone number"}
                      name="phone"
                    />
                  </div>
                  <div className="mb-3">
                    <TextInputComp
                      LabelText={"Email address"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-2"}
                      placeholder={"Enter Email"}
                      name="email"
                    />
                  </div>
                </div>
                <div className="col-12 col-lg-5">
                  <div className="mb-3">
                    <TextInputComp
                      LabelText={"Address"}
                      borderColor="#A1A0A0"
                      LabelClassName={"fw-2"}
                      placeholder={"Enter Address"}
                      name="address"
                    />
                  </div>
                  <div className="mb-4">
                    <FileUploaderComp
                      LabelText={"Profile Image"}
                      labelClassName={"fw-2"}
                      accept="image/*"
                      bodyText={
                        <div className="d-flex align-items-center">
                          <ImageLoaderUpdate
                            src={"/FileUploader/upload.svg"}
                            style={{ width: "60%" }}
                            className="me-3"
                          />

                          <div className="fw-2" style={{ color: "#6B6B6B" }}>
                            {useCooperativeLogo
                              ? "Using Cooperative Logo"
                              : "Click to Upload Image"}
                          </div>
                        </div>
                      }
                      onChange={handlePhotoUpload}
                    />
                    {useCooperativeLogo && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Branch will use the cooperative's main logo instead of
                          a custom image.
                        </small>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="useCooperativeLogo"
                        checked={useCooperativeLogo}
                        onChange={(e) =>
                          setUseCooperativeLogo(e.target.checked)
                        }
                        style={{
                          width: "22px",
                          height: "22px",
                          marginTop: "0",
                        }}
                      />
                      <label
                        className="form-check-label ms-3"
                        htmlFor="useCooperativeLogo"
                      >
                        use cooperative logo
                      </label>
                    </div>
                  </div>
                </div>
              </CardComp>
              <div className="">
                <button
                  type="submit"
                  className="text-white h-10 bg-[var(--color-primary)] px-4 rounded hover:scale-105 disabled:opacity-50"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating branch..." : "Create Branch"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
}
